import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { CompanionCard } from '@components/Cards';
import { CarouselLeftArrowIcon } from '@components/Icons/CarouselLeftArrowIcon';
import { CarouselRightArrowIcon } from '@components/Icons/CarouselRightArrowIcon';
import { CompanionsIcon } from '@components/Icons/CompanionsIcon';
import { RightArrowicon } from '@components/Icons/RightArrowIcon';
import { useStationMode } from '@hooks/station';
import { useCompanionsInfiniteScroll } from '@hooks/useCompanionsInfiniteScroll';
import {
  addFavouriteCompanion,
  getFavouriteCompanionsPaginated,
  removeFavouriteCompanion,
} from '@services/api';
import { useAuth } from 'react-oidc-context';
import { Companion } from 'src/types';

export const HomeCompanions = () => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isStationMode = useStationMode();
  const { isAuthenticated } = useAuth();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // State for tracking favorited companions
  const [favoritedIds, setFavoritedIds] = useState<Set<string>>(new Set());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  // Use infinite scroll hook for companions with horizontal scroll container
  const {
    companions: displayItems,
    loading: isLoading,
    error: displayError,
    hasMore,
    sentinelRef,
  } = useCompanionsInfiniteScroll({
    rootMargin: '0px 600px 0px 0px',
    scrollContainerRef: scrollContainerRef,
    enabled: true,
  });

  // Fetch user's favorite companions on mount (if authenticated)
  useEffect(() => {
    if (!isAuthenticated) {
      setFavoritedIds(new Set());
      return;
    }

    const fetchFavorites = async () => {
      try {
        // Fetch all favorites (use high limit to get all)
        const result = await getFavouriteCompanionsPaginated(100, 0);
        const ids = new Set<string>();
        result?.items?.forEach(
          (item: { id?: string; companionId?: string; slug?: string }) => {
            // API returns id which IS the companionId (= mentorId)
            if (item.id) ids.add(item.id);
          },
        );
        setFavoritedIds(ids);
      } catch (error) {
        console.error('Error fetching favorite companions:', error);
      }
    };

    fetchFavorites();
  }, [isAuthenticated]);

  // Handle favorite toggle
  const handleFavoriteToggle = useCallback(
    async (companion: Companion) => {
      if (!companion.mentorId) return;

      const mentorId = companion.mentorId;
      const isFavorited = favoritedIds.has(mentorId);

      // Set loading state
      setLoadingIds((prev) => new Set(prev).add(mentorId));

      try {
        if (isFavorited) {
          // Remove from favorites
          await removeFavouriteCompanion(mentorId);
          setFavoritedIds((prev) => {
            const next = new Set(prev);
            next.delete(mentorId);
            return next;
          });
        } else {
          // Add to favorites
          await addFavouriteCompanion(mentorId, companion.slug || '');
          setFavoritedIds((prev) => new Set(prev).add(mentorId));
        }
      } catch (error) {
        console.error('Error toggling companion favorite:', error);
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(mentorId);
          return next;
        });
      }
    },
    [favoritedIds],
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    setCanScrollLeft(scrollLeft > 25);
    setCanScrollRight(scrollLeft < maxScroll - 25);
  };

  return (
    <div id="companions" className="w-full mb-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 font-[NapsterSans] px-4 sm:px-6">
        <div className="flex gap-[8px] items-center hover-transition">
          <span className="text-white">
            <CompanionsIcon width={24} height={24} />
          </span>
          <Link
            to="/companions"
            className="flex gap-[8px] items-center text-[18px] text-white hover:text-[#fcfcfc] transition-all duration-500 ease-in-out font-bold leading-[normal] cursor-pointer group">
            {t('home.companions')}
            <span>
              <RightArrowicon />
            </span>
          </Link>
        </div>
        <Link
          to="/companions"
          className="text-xs sm:text-sm text-white hover:text-[#fcfcfc] hover:underline transition-all duration-300 ease-in-out cursor-pointer">
          {t('home.showAll')}
        </Link>
      </div>

      {/* Initial Loading State */}
      {isLoading && displayItems.length === 0 && (
        <div className="flex gap-[16px] pb-[30px] h-full overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar scrollbar-hide select-none scroll-pl-4 sm:scroll-pl-6">
          <div className="flex-shrink-0 w-4 sm:w-6"></div>
          {[...Array(4)].map((_, index) => (
            <div
              key={`loading-${index}`}
              className={`flex-shrink-0 snap-start ${
                isStationMode ? 'w-[282px]' : 'w-[371px]'
              }`}>
              <div
                className={`w-full rounded-[16px] bg-gray-800 animate-pulse relative ${
                  isStationMode ? 'h-[395px]' : 'h-[520px]'
                }`}>
                <div className="absolute top-0 left-0 w-full h-full rounded-[16px] bg-gray-700"></div>
                <div className="absolute bottom-6 left-4 right-4 z-[100]">
                  <div className="h-6 bg-gray-600 rounded mb-2"></div>
                  <div className="h-4 bg-gray-600 rounded w-3/4 mb-4"></div>
                </div>
              </div>
            </div>
          ))}
          <div className="flex-shrink-0 w-4 sm:w-6"></div>
        </div>
      )}

      {/* Error State */}
      {displayError && !isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="text-red-400 mb-4">
              <svg
                className="w-12 h-12 mx-auto mb-4"
                fill="currentColor"
                viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-lg font-medium">{t('home.failedToLoadCompanions')}</p>
              <p className="text-sm text-red-300 mt-1">{displayError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Slider Content */}
      {!displayError && displayItems.length > 0 && (
        <div className="relative w-full group">
          {/* Left Arrow */}
          {canScrollLeft && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (scrollContainerRef.current) {
                  scrollContainerRef.current.scrollBy({
                    left: -400,
                    behavior: 'smooth',
                  });
                }
              }}
              className="absolute left-9 top-1/2 -translate-y-1/2 z-[300] hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 transition-all duration-200 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
              aria-label={t('aria.previousSlide')}>
              <CarouselLeftArrowIcon />
            </button>
          )}

          {/* Right Arrow */}
          {canScrollRight && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (scrollContainerRef.current) {
                  scrollContainerRef.current.scrollBy({
                    left: 400,
                    behavior: 'smooth',
                  });
                }
              }}
              className="absolute right-9 top-1/2 -translate-y-1/2 z-[300] hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 transition-all duration-200 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
              aria-label={t('aria.nextSlide')}>
              <CarouselRightArrowIcon />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            className="flex gap-[16px] pb-[30px] h-full overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar scrollbar-hide select-none scroll-pl-4 sm:scroll-pl-6"
            onScroll={handleScroll}>
            {/* Initial spacer */}
            <div className="flex-shrink-0 w-4 sm:w-6"></div>
            {displayItems.map((companion: Companion, index: number) => {
              const companionId =
                companion.mentorId || companion.slug || index.toString();

              return (
                <CompanionCard
                  key={`companion-${companionId}`}
                  companion={companion}
                  className={isStationMode ? 'min-w-[282px]' : 'min-w-[371px]'}
                  compactSize={isStationMode}
                  showFavoriteButton={isAuthenticated}
                  isFavorited={
                    companion.mentorId
                      ? favoritedIds.has(companion.mentorId)
                      : false
                  }
                  isLoading={
                    companion.mentorId
                      ? loadingIds.has(companion.mentorId)
                      : false
                  }
                  onFavoriteClick={(e) => {
                    e.stopPropagation();
                    handleFavoriteToggle(companion);
                  }}
                />
              );
            })}

            {/* Infinite scroll sentinel */}
            {hasMore && displayItems.length > 0 && !isLoading && (
              <div ref={sentinelRef} className="flex snap-start"></div>
            )}

            {/* Loading more indicator */}
            {hasMore && displayItems.length > 0 && (
              <div
                className={`flex-shrink-0 snap-start ${
                  isStationMode ? 'w-[282px]' : 'w-[371px]'
                }`}>
                <div
                  className={`w-full rounded-[16px] bg-gray-800 animate-pulse relative ${
                    isStationMode ? 'h-[395px]' : 'h-[520px]'
                  }`}>
                  <div className="absolute top-0 left-0 w-full h-full rounded-[16px] bg-gray-700"></div>
                  <div className="absolute bottom-6 left-4 right-4 z-[100]">
                    <div className="h-6 bg-gray-600 rounded mb-2"></div>
                    <div className="h-4 bg-gray-600 rounded w-3/4 mb-4"></div>
                  </div>
                </div>
              </div>
            )}

            {/* Final spacer */}
            <div className="flex-shrink-0 w-4 sm:w-6"></div>
          </div>
        </div>
      )}
    </div>
  );
};
